import { nextOccurrence, parseSkippedDates } from './recurrence';
import {
    buildProjectedRecurringTransactionId,
    parseProjectedRecurringTransactionId,
} from './virtualTransactionId';

// O codec de id da Ocorrência projetada mora no lar do vocabulário virtual (virtualTransactionId.ts).
// Re-exportado aqui pra não quebrar quem já importa de '@/lib/projectedRecurringTransactions'.
export { buildProjectedRecurringTransactionId, parseProjectedRecurringTransactionId };

/**
 * Tag herdada da Regra. Mesma forma de `TransactionTagInfo`, redeclarada aqui porque o motor
 * é puro e mora em `lib/` — importar de `db/repositories` seria dependência pra trás (ADR 0009).
 */
export type ProjectedRecurringTag = {
    id: string;
    name: string;
    color: string;
};

export type ProjectedRecurringRule = {
    id: string;
    type: string;
    amount: number;
    description?: string | null;
    notes?: string | null;
    time?: string | null;
    /**
     * Tags da SÉRIE (`recurring_transaction_tags`). Opt-in: quem monta as regras só busca
     * quando a leitura exibe ou agrupa por tag — o forecast soma dinheiro e não paga o SELECT.
     * Ver ADR 0101 e CONTEXT.md ("Tag numa recorrência").
     */
    tags?: ProjectedRecurringTag[] | null;
    /**
     * Lançamento ignorado da SÉRIE (ADR 0169): a projetada herda o flag da Regra, como a
     * materializada. OBRIGATÓRIO de propósito — quem monta a regra e esquece a coluna faria a
     * projetada nascer contando na análise, e o furo só aparece no relatório do mês que vem.
     */
    isIgnored: number | null;
    frequency: string;
    interval: number;
    weekdays?: string | null;
    monthlyMode?: string | null;
    startDate: string;
    nextOccurrence: string;
    endDate?: string | null;
    skippedDates?: string | null;
    accountId?: string | null;
    creditCardId?: string | null;
    categoryId?: string | null;
    subcategoryId?: string | null;
    transferDestinationAccountId?: string | null;
    accountName?: string | null;
    accountColor?: string | null;
    accountIcon?: string | null;
    creditCardName?: string | null;
    categoryName?: string | null;
    categoryIcon?: string | null;
    categoryColor?: string | null;
    /**
     * Nome da subcategoria da Regra. Ficou de fora por meses: a ocorrência projetada nascia
     * com `subcategoryId` e `subcategoryName: null`, e a lista em "Subcategoria" caía pra
     * categoria da 2ª ocorrência em diante (Denis, 22/09/2026).
     */
    subcategoryName?: string | null;
    destinationAccountName?: string | null;
    destinationAccountColor?: string | null;
};

export function buildProjectedRecurringTransactions(
    rules: ProjectedRecurringRule[],
    startDate: string,
    endDate: string,
    materializedKeys: Set<string>,
) {
    const projected = [];

    for (const rule of rules) {
        const skipped = parseSkippedDates(rule.skippedDates);
        let current = rule.nextOccurrence;
        let guard = 1000;

        while (current <= endDate && guard > 0) {
            guard -= 1;

            if (rule.endDate && current > rule.endDate) {
                break;
            }

            if (current >= startDate && !skipped.has(current) && !materializedKeys.has(`${rule.id}:${current}`)) {
                projected.push({
                    id: buildProjectedRecurringTransactionId(rule.id, current),
                    type: rule.type,
                    amount: rule.amount,
                    description: rule.description ?? '',
                    date: current,
                    time: rule.time ?? null,
                    notes: rule.notes ?? null,
                    // A Regra é dona da tag e a ocorrência HERDA — inclusive a projetada, que
                    // nunca existe em transaction_tags (id virtual, ADR 0011). Passthrough puro:
                    // o motor não busca nada, só repassa o que o call site carregou (ADR 0101).
                    tags: rule.tags ?? undefined,
                    accountId: rule.accountId ?? null,
                    creditCardId: rule.creditCardId ?? null,
                    invoiceId: null,
                    invoiceReferenceMonth: null,
                    transferDestinationAccountId: rule.transferDestinationAccountId ?? null,
                    transferId: null,
                    categoryId: rule.categoryId ?? null,
                    subcategoryId: rule.subcategoryId ?? null,
                    isRecurring: 1,
                    recurringId: rule.id,
                    isHiddenFromSubs: 0,
                    // Ignorado (ADR 0169): herdado da Regra, como a nota e a tag. Quem soma
                    // análise filtra por ele (isIgnoredTransaction); saldo e fatura não.
                    isIgnored: rule.isIgnored === 1 ? 1 : 0,
                    installmentId: null,
                    installmentNumber: null,
                    installmentTotal: null,
                    adjustmentType: null,
                    isConfirmed: 0,
                    createdAt: `${current}T00:00:00.000Z`,
                    updatedAt: `${current}T00:00:00.000Z`,
                    deletedAt: null,
                    accountName: rule.accountName ?? null,
                    accountColor: rule.accountColor ?? null,
                    accountIcon: rule.accountIcon ?? null,
                    categoryName: rule.categoryName ?? null,
                    categoryIcon: rule.categoryIcon ?? null,
                    categoryColor: rule.categoryColor ?? null,
                    subcategoryName: rule.subcategoryName ?? null,
                    destinationAccountName: rule.destinationAccountName ?? null,
                    destinationAccountColor: rule.destinationAccountColor ?? null,
                    isProjected: true,
                });
            }

            const next = nextOccurrence(current, rule);
            if (next <= current) {
                break;
            }
            current = next;
        }
    }

    return projected;
}
